import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { CookieConsent, CONSENT_VERSION, getConsentStorageKey, DEFAULT_CONSENT } from "@/config/consent";
import { useClient } from "@/contexts/ClientContext";

type ConsentContextType = {
  consent: CookieConsent | null;
  setConsent: (consent: CookieConsent) => void;
  hasConsent: (category: keyof CookieConsent["choices"]) => boolean;
  loadApprovedScripts: () => void;
  needsConsent: boolean;
  locale: "fr" | "en";
  setLocale: (locale: "fr" | "en") => void;
};

const ConsentContext = createContext<ConsentContextType | undefined>(undefined);

export const ConsentProvider = ({ children }: { children: ReactNode }) => {
  const { client } = useClient();
  const [consent, setConsentState] = useState<CookieConsent | null>(null);
  const [needsConsent, setNeedsConsent] = useState(false);
  const [locale, setLocale] = useState<"fr" | "en">("fr");
  const [scriptsLoaded, setScriptsLoaded] = useState({
    analytics: false,
    product_improvement: false,
    marketing: false,
  });

  useEffect(() => {
    // Wait for client to be loaded before checking consent
    if (!client) {
      return;
    }

    // Load consent from localStorage using client-scoped key
    const storageKey = getConsentStorageKey(client.id);
    const stored = localStorage.getItem(storageKey);
    
    if (stored) {
      try {
        const parsed: CookieConsent = JSON.parse(stored);
        
        // Check if version matches
        if (parsed.version === CONSENT_VERSION) {
          setConsentState(parsed);
          setLocale(parsed.locale);
          setNeedsConsent(false);
        } else {
          // Version mismatch - needs new consent
          setNeedsConsent(true);
        }
      } catch (error) {
        console.error("[Consent] Failed to parse stored consent:", error);
        setNeedsConsent(true);
      }
    } else {
      // No consent stored
      setNeedsConsent(true);
    }
  }, [client]);

  const setConsent = (newConsent: CookieConsent) => {
    if (!client) {
      console.error("[Consent] Cannot save consent without client context");
      return;
    }

    const consentToStore: CookieConsent = {
      ...newConsent,
      version: CONSENT_VERSION,
      timestamp: new Date().toISOString(),
    };

    // Store consent with client-scoped key for multi-tenant isolation
    const storageKey = getConsentStorageKey(client.id);
    localStorage.setItem(storageKey, JSON.stringify(consentToStore));
    setConsentState(consentToStore);
    setNeedsConsent(false);
    
    // Load approved scripts after consent is given
    loadApprovedScriptsInternal(consentToStore);
  };

  const hasConsent = (category: keyof CookieConsent["choices"]): boolean => {
    if (!consent) return category === "essential";
    return consent.choices[category] === true;
  };

  const loadApprovedScriptsInternal = (currentConsent: CookieConsent) => {
    if (currentConsent.choices.analytics && !scriptsLoaded.analytics) {
      setScriptsLoaded(prev => ({ ...prev, analytics: true }));
    }
    if (currentConsent.choices.product_improvement && !scriptsLoaded.product_improvement) {
      setScriptsLoaded(prev => ({ ...prev, product_improvement: true }));
    }
    if (currentConsent.choices.marketing && !scriptsLoaded.marketing) {
      setScriptsLoaded(prev => ({ ...prev, marketing: true }));
    }
  };

  const loadApprovedScripts = () => {
    if (consent) {
      loadApprovedScriptsInternal(consent);
    }
  };

  // Auto-load scripts when consent changes
  useEffect(() => {
    if (consent) {
      loadApprovedScripts();
    }
  }, [consent]);

  return (
    <ConsentContext.Provider
      value={{
        consent,
        setConsent,
        hasConsent,
        loadApprovedScripts,
        needsConsent,
        locale,
        setLocale,
      }}
    >
      {children}
    </ConsentContext.Provider>
  );
};

export const useConsent = () => {
  const context = useContext(ConsentContext);
  if (context === undefined) {
    throw new Error("useConsent must be used within a ConsentProvider");
  }
  return context;
};
